<template>
  <div class="main-collection__container container">
    <div
      class="main-collection__action-bar"
      :class="{
        'main-collection__action-bar--sticky': scroll.sticky,
        'main-collection__action-bar--slide': scroll.slideDown,
      }"
      ref="actionBar"
    >
      <!-- Desktop Filters -->
      <div
        v-if="!isMobile"
        ref="filters"
        class="main-collection__filters main-collection__filters--v1 is-hidden--mobile"
        :class="{
          [$class('active')]: collectionVariables.productFilters.length,
          [$class('open')]: activeFilter != null,
        }"
      >
        <template
          v-for="filter of filters"
          :key="filter.id"
        >
          <div
            v-if="filter.type === 'LIST' && filter.id !== 'filter.p.vendor'"
            class="main-collection__filter"
          >
            <button
              class="main-collection__filter-button"
              :class="{
                [$class('active')]: activeFilter === filter.id,
                'is-selected': isSelected(filter.id),
              }"
              type="button"
              @click="toggleFilter(filter.id)"
            >
              <span
                class="main-collection__filter-button-label text-body-m-bold"
                v-text="filter.label"
              />

              <span class="main-collection__filter-icon">
                <icon-plus v-if="activeFilter !== filter.id" />
                <icon-minus v-else />
              </span>
            </button>

            <div
              v-show="activeFilter === filter.id"
              class="main-collection__filter-values container"
            >
              <div class="main-collection__filter-values-wrapper">
                <template
                  v-for="value of filter.values"
                  :key="value.id"
                >
                  <div
                    class="main-collection__filter-item checkbox-input"
                    :class="{
                      [$class('disabled')]: value.count <= 0 && !isChecked(value.input),
                    }"
                  >
                    <input
                      :id="value.id"
                      class="checkbox-input__input visually-hidden"
                      type="checkbox"
                      :name="filter.id"
                      :value="value.id"
                      :checked="isChecked(value.input)"
                      @change="handleFilter(value.input)"
                    >

                    <label
                      class="checkbox-input__label text-body-m-regular"
                      :for="value.id"
                      v-text="`${value.label} (${value.count})`"
                    />
                  </div>
                </template>
              </div>
            </div>
          </div>

          <!-- Price Range Filter -->
          <div
            v-if="filter.type === 'PRICE_RANGE' && priceRange.value"
            class="main-collection__filter"
          >
            <button
              class="main-collection__filter-button"
              :class="{
                [$class('collapsed')]: !activeFilter,
                [$class('active')]: activeFilter === filter.id,
                'is-selected': priceRange.value[0] != priceRange.min
                  || priceRange.value[1] != priceRange.max
              }"
              type="button"
              @click="toggleFilter(filter.id)"
            >
              <span
                class="main-collection__filter-button-label text-body-m-bold"
                v-text="filter.label"
              />

              <icon-plus v-if="activeFilter !== filter.id" />
              <icon-minus v-else />
            </button>

            <div
              v-show="activeFilter === filter.id"
              class="main-collection__filter-values container"
              :class="{
                'main-collection__filter-values--price':
                  filter.type === 'PRICE_RANGE',
              }"
            >
              <div class="main-collection__filter-values-wrapper">
                <slider
                  v-model="priceRange.value"
                  lazy
                  :merge="20"
                  :step="5"
                  tooltip-position="bottom"
                  :format="priceRange.format"
                  :min="priceRange.min"
                  :max="priceRange.max"
                  :classes="{
                    tooltipBottom: 'slider-tooltip-bottom text-body-xs-regular',
                  }"
                  @change="handlePriceFilter"
                />
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- Mobile Filters -->
      <button
        v-if="isMobile"
        class="main-collection__filter-trigger is-hidden--desktop"
        type="button"
        @click="
          openOverlay({
            ignoreDismissed: true,
            namespace: 'filterDrawer',
          })
        "
      >
        <span
          class="text-body-s-bold"
          v-text="$string('collection.filter_sort')"
        />

        <icon-filter />
      </button>

      <teleport to="body">
        <overlay
          key="filterDrawer"
          direction="right"
          namespace="filterDrawer"
          type="drawer"
          block-class="filter-drawer"
          show-close="true"
        >
          <template #title>
            <template v-if="filterStep === 1">
              {{ $string("search.filter") }}
            </template>

            <div
              v-else
              class="filter-drawer__header-inner"
            >
              <button
                class="filter-drawer__header-back"
                type="button"
                @click="filterStep = 1"
              >
                <icon-chevron-left-edge />
              </button>

              <div
                class="filter-drawer__header-title text-body-l-bold"
                v-text="
                  currentFilters.label || $string('search.sort.title_mobile')
                "
              />
            </div>
          </template>

          <template #body>
            <div class="filter-drawer__body-inner">
              <div
                v-if="filterStep === 1"
                class="filter-drawer__item-list"
              >
                <button
                  class="filter-drawer__filter-button"
                  type="button"
                  @click="(currentFilters = 'sort'), (filterStep = 2)"
                >
                  <span
                    class="text-heading-m"
                    v-text="$string('search.sort.title_mobile')"
                  />

                  <icon-chevron-right />
                </button>

                <template
                  v-for="filter of filters"
                  :key="filter.id"
                >
                  <button
                    v-if="filter.id !== 'filter.p.vendor'"
                    class="filter-drawer__filter-button"
                    type="button"
                    @click="(currentFilters = filter), (filterStep = 2)"
                  >
                    <span
                      class="text-heading-m"
                      v-text="filter.label"
                    />

                    <icon-chevron-right />
                  </button>
                </template>
              </div>

              <template v-if="filterStep === 2 && currentFilters">
                <div
                  v-if="currentFilters.type === 'LIST'"
                  class="main-collection__filter-values"
                >
                  <template
                    v-for="value of currentFilters.values"
                    :key="value.id"
                  >
                    <div
                      class="filter-drawer__value-list checkbox-input"
                      :class="{
                        [$class('disabled')]: value.count <= 0 && !isChecked(value.input),
                      }"
                    >
                      <input
                        :id="`mobile-${value.id}`"
                        class="checkbox-input__input visually-hidden"
                        type="checkbox"
                        :name="`mobile-${value.id}`"
                        :value="value.id"
                        :checked="isChecked(value.input)"
                        @change="handleFilter(value.input)"
                      >

                      <label
                        class="checkbox-input__label text-body-m-regular"
                        :for="`mobile-${value.id}`"
                        v-text="`${value.label} (${value.count})`"
                      />
                    </div>
                  </template>
                </div>

                <div
                  v-if="currentFilters.type === 'PRICE_RANGE'"
                  class="main-collection__filter-values"
                  :class="{
                    'main-collection__filter-values--price':
                      currentFilters.type === 'PRICE_RANGE',
                  }"
                >
                  <slider
                    v-model="priceRange.value"
                    lazy
                    :merge="20"
                    :step="5"
                    tooltip-position="bottom"
                    :format="priceRange.format"
                    :min="priceRange.min"
                    :max="priceRange.max"
                    :classes="{
                      tooltipBottom:
                        'slider-tooltip-bottom text-body-xs-regular',
                    }"
                    @change="handlePriceFilter"
                  />
                </div>
              </template>

              <div v-if="filterStep === 2 && currentFilters === 'sort'">
                <div
                  v-for="option of sortOptions"
                  :key="option.value.sort"
                  class="
                    filter-drawer__value-list
                    radio-input
                  "
                >
                  <input
                    :id="option.value.id"
                    v-model="currentSort"
                    class="radio-input__input visually-hidden"
                    type="radio"
                    name="sort"
                    :value="option.value"
                    :checked="currentSort.id === option.value.id"
                    @change="handleSort"
                  >

                  <label
                    class="
                      radio-input__label
                      radio-input__label--small
                      text-body-m-regular
                    "
                    :for="option.value.id"
                    v-text="option.title"
                  />
                </div>
              </div>
            </div>

            <div class="filter-drawer__footer">
              <btn
                type="button"
                modifiers="secondary block center"
                :label="$string('search.filter_clear')"
                @click="resetFilters"
              />

              <btn
                type="button"
                modifiers="primary block center"
                :label="$string('search.filter_apply')"
                @click="applyFilters"
              />
            </div>
          </template>
        </overlay>
      </teleport>

      <div class="main-collection__controls-content">
        <p
          v-if="
            collection.settings.showSingleProduct &&
            collectionProducts.length > 0
              || collectionVariables.productFilters.length
          "
          class="
            main-collection__controls-count
            text-body-xs-regular text-body-m-regular-desktop
          "
          v-text="$string('collection.item_count', {
            pluralise: collectionProducts.length,
            replace: {
              num: collectionProducts.length,
            },
          })
          "
        />
        <p
          v-else-if="
            collectionData?.totalCount > 0
              || collectionVariables.productFilters.length
          "
          class="
            main-collection__controls-count
            text-body-xs-regular text-body-m-regular-desktop
          "
          v-text="$string('collection.item_count', {
            pluralise: collectionData?.totalCount,
            replace: {
              num: collectionData?.totalCount,
            },
          })
          "
        />

        <div
          v-if="!isMobile"
          class="main-collection__sort-container is-hidden--mobile"
        >
          <label
            class="text-body-m-regular"
            for="sort"
            v-text="`${$string('collection.sort_by')}:`"
          />

          <select
            id="sort"
            ref="mainSelect"
            v-model="currentSort"
            class="main-collection__select text-body-m-bold"
            @change="handleSort"
          >
            <option
              v-for="option of sortOptions"
              :key="option.value.sort"
              :value="option.value"
              v-text="option.title"
            />
          </select>

          <select
            ref="selectResize"
            class="
              main-collection__select
              main-collection__select--resize
              text-body-m-bold
            "
            aria-hidden="true"
          >
            <option
              v-text="selectResizeOption || sortOptions[0].title"
            />
          </select>

          <span class="main-collection__sort-icon">
            <icon-chevron-down />
          </span>
        </div>
      </div>
    </div>

    <div
      class="main-collection__grid grid"
      :class="{ 'main-collection__grid--sticky-padding': scroll.sticky }"
      ref="collectionGrid"
    >
      <!-- Empty loading state -->
      <template
        v-if="
          !collectionProducts.length
            && !collectionVariables.productFilters.length
        "
      >
        <div
          v-for="index of collection.productsPerPage"
          :key="index"
          class="col xs3 l3"
        >
          <product-card
            :product="false"
            :sizes="sizes"
          />
        </div>
      </template>

      <!-- Product grid -->
      <template
        v-else
        v-for="(product, index) in collectionProducts"
        :key="product.id"
      >
        <!-- Ad Block -->
        <div
          v-if="getAdBlockForIndex(index)"
          class="col xs3 l3"
          :class="{ 'main-collection__ad-block': !designMode }"
          :key="'ad-' + index"
        >
          <ad-block
            :block="getAdBlockForIndex(index)"
          />
        </div>

        <!-- Product card -->
        <div class="col xs3 l3">
          <product-card
            v-if="state === 'ready'"
            :product="product"
            :show-secondary-image="collection.settings.showRolloverImage"
            :show-collection-image="collection.settings.showCollectionImage"
            :show-dynamic-swatches="collection.settings.showDynamicSwatches"
            :collection-page="true"
            :starRatingInstanceId="collection.starRatingInstanceId"
            :product-subtitles="collection.productSubtitles"
            :mobile="isMobile"
            :sizes="sizes"
          />
          <product-card
            v-else
            :product="false"
            :sizes="sizes"
          />
        </div>
      </template>
    </div>

    <div
      v-if="collectionProducts.length > 0"
      class="main-collection__footer"
    >
      <p
        v-if="collection.settings.showSingleProduct"
        class="
          main-collection__footer-count
          text-body-xs-regular text-body-s-regular-desktop
        "
        v-text="
          $string('collection.total_count', {
            pluralise: collectionProducts.length,
            replace: {
              num: collectionProducts.length,
              total: collectionProducts.length,
            },
          })
        "
      />
      <p
        v-else
        class="
          main-collection__footer-count
          text-body-xs-regular text-body-s-regular-desktop
        "
        v-text="
          $string('collection.total_count', {
            pluralise: collectionData?.totalCount,
            replace: {
              num: collectionProducts.length,
              total: collectionData?.totalCount,
            },
          })
        "
      />

      <btn
        v-if="collectionData?.hasNextPage"
        class="main-collection__footer-btn"
        :label="$string('collection.load_more')"
        modifiers="primary"
        @click="() => queryCollection(true)"
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Main collection (main-collection)
 * -----------------------------------------------------------------------------
 * Main collection page component.
 *
 * @param {Object|Boolean} collection - Canvas formatted collection object.
 *
 */

import { nextTick } from 'vue'
import { mapActions, mapState } from 'vuex'
import { values as breakpoints } from '~/config/breakpoints'
import Slider from '@vueform/slider'

import collectionQuery from '~graphql/collection.gql'
import { formatCollection } from '~/helpers/format'

import Btn from '~global/btn/btn'
import Overlay from '~global/overlay/overlay'
import ProductCard from '~async/product-card/product-card'

import IconChevronDown from '~icons/directional-navigation/chevron/down.svg'
import IconFilter from '~icons/general/filter.svg'
import IconPlus from '~icons/directional-navigation/plus.svg'
import IconMinus from '~icons/directional-navigation/minus.svg'
import IconChevronRight from '~icons/directional-navigation/chevron/right.svg'
import IconChevronLeftEdge from '~icons/directional-navigation/chevron/left-edge.svg'
import { throttle } from '~/helpers/general'
import AdBlock from '~async/main-collection/ad-block'

export default {
  name: 'MainCollection',

  components: {
    Btn,
    IconChevronDown,
    IconChevronLeftEdge,
    IconChevronRight,
    IconFilter,
    IconMinus,
    IconPlus,
    Overlay,
    ProductCard,
    Slider,
    AdBlock,
  },

  props: {
    collection: {
      type: Object,
      default: () => ({
        handle: '',
        id: '',
        productsPerPage: 0,
        starRatingInstanceId: false,
        settings: {
          show_colour: true,
          show_price: true,
          show_product_type: true,
          show_size: true,
          showDynamicSwatches: false,
          showSingleProduct: false,
          showCollectionImage: false,
          showRolloverImage: false,
        },
        productSubtitles: [],
        adBlocks: [],
        totalProducts: '',
      }),
    },
  },

  data() {
    return {
      activeFilter: null,
      currentFilters: [],
      collectionData: {
        currentPage: 1,
        totalCount: 0,
      },
      collectionProducts: [],
      processedTitles: [],
      collectionVariables: {
        after: null,
        country: cnvs.store.country,
        first: this.collection.productsPerPage,
        handle: this.collection.handle,
        language: cnvs.store.language,
        productFilters: [],
      },
      currentSort: {
        id: 'best-sellers',
        sort: 'COLLECTION_DEFAULT',
        reverse: false,
      },
      designMode: this.$variable('request.designMode'),
      filters: [],
      filterStep: 1,
      pageNumber: 1,
      priceRange: {
        value: null,
        min: 0,
        max: null,
        format: {
          prefix: this.$variable('store.currencySymbol'),
          decimals: 0,
        },
      },
      state: 'loading',
      selectResizeOption: '',
      sortOptions: [
        {
          title: this.$string('collection.sort_options.best_sellers'),
          value: {
            id: 'best-sellers',
            sort: 'COLLECTION_DEFAULT',
            reverse: false,
          },
        },
        {
          title: this.$string('collection.sort_options.featured'),
          value: {
            id: 'featured',
            sort: 'MANUAL',
            reverse: false,
          },
        },
        {
          title: this.$string('collection.sort_options.newest'),
          value: {
            id: 'newest',
            sort: 'CREATED',
            reverse: true,
          },
        },
        {
          title: this.$string('collection.sort_options.price_low'),
          value: {
            id: 'price-low',
            sort: 'PRICE',
            reverse: false,
          },
        },
        {
          title: this.$string('collection.sort_options.price_high'),
          value: {
            id: 'price-high',
            sort: 'PRICE',
            reverse: true,
          },
        },
      ],
      isCollectionGridInView: false,
      scroll: {
        sticky: false,
        slideDown: false,
      },
      gridIntersectionScrollY: 0,
      lastScrollY: 0,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
      settings: (state) => state.index.settings,
    }),

    /**
     * Compute responsive image sizes.
     * @returns {String}
     */
    sizes() {
      return `(min-width: ${breakpoints.l}) 33vw, 100vw`
    },
  },

  watch: {

    /**
     * Watch page number to update URL params and item count.
     */
    pageNumber() {
      this.updateUrlPageParams(this.pageNumber)
    },

    /**
     * Watch sort object to update collection sort.
     */
    currentSort() {
      this.selectResizeOption = this.sortOptions.find(
        option => option.value.id === this.currentSort.id,
      )?.title

      this.updateUrlSortParams(this.currentSort)
    },

    /**
     * Watch sort object to update collection sort.
     */
    selectResizeOption() {
      if (!this.isMobile) {
        nextTick().then(() => {
          this.getSelectWidth()
        })
      }
    },
  },

  mounted() {
    this.getValuesFromParams()
    nextTick().then(() => {
      this.getSelectWidth()
    })

    this.queryCollection()
    this.setEventListeners()
    this.setStickyFitler()
  },

  beforeDestroy() {
    if (this.collectionGridObserver) {
      this.collectionGridObserver.disconnect();
    }
    window.removeEventListener("scroll", this.handleScroll);
  },

  methods: {
    /**
     * Returns an ad block object matching with index.
     * @param {Number} index - Index of collection.
     *
     */
    getAdBlockForIndex(index) {
      if (!this.collection.adBlocks) return null;

      const key = this.isMobile ? 'indexMobile' : 'indexDesktop'

      return this.collection.adBlocks.find(block => block[key] === index) || null
    },

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
    }),

    /**
     * IntersectionObserver to check when to set filters sticky.
     */
    setStickyFitler(){
      const collectionGridOptions = {
        root: null,
        rootMargin: "0px 0px -100% 0px",
        threshold: 0,
      };

      this.collectionGridObserver = new IntersectionObserver(
        this.handleCollectionGridIntersection,
        collectionGridOptions
      );

      const collectionGrid = this.$refs.collectionGrid;

      if (collectionGrid) {
        this.collectionGridObserver.observe(collectionGrid);
      }
    },

    /**
     * On scroll even to change slideDown value.
     * @param {Array} entries - list of IntersectionObserverEntry objects
     */
    handleCollectionGridIntersection(entries) {
      entries.forEach(entry => {
        this.isCollectionGridInView = entry.isIntersecting;

        if (this.isCollectionGridInView) {
          this.scroll.sticky = true;
          this.gridIntersectionScrollY = window.scrollY;
          this.lastScrollY = window.scrollY;
        } else {
          this.scroll.sticky = false;
          this.scroll.slideDown = false;
        }
      });
    },

    /**
     * On scroll even to change slideDown value.
     */
    handleScroll() {
      const currentScrollY = window.scrollY;
      const scrolledDown = currentScrollY - this.lastScrollY > 50;
      const scrolledUp = this.lastScrollY - currentScrollY > 0;

      if (this.scroll.sticky && scrolledDown) {
        this.scroll.slideDown = true;
      }
      else if (this.scroll.sticky && scrolledUp) {
        this.scroll.slideDown = false;
        this.activeFilter = null
      }

      this.lastScrollY = currentScrollY;
    },

    /**
     * Set event listeners
     */
    setEventListeners() {
      window.addEventListener('click', (event) => {
        const filters = this.$refs.filters

        if (this.isMobile || !this.activeFilter || !filters) {
          return
        }

        if (filters !== event.target && !filters.contains(event.target)) {
          this.activeFilter = null
        }
      })

      window.addEventListener(
        'scroll',
        throttle(() => this.handleScroll(), this.settings.throttle)
      )
    },

    /**
     * Get values from URL params on page load
     */
    getValuesFromParams() {
      const currentUrl = new URL(window.location)
      const params = new URLSearchParams(currentUrl.search)

      if (params.get('sort')) {
        const sort = params.get('sort')

        this.currentSort =
          this.sortOptions.find((option) => option.value.id === sort)?.value ||
          this.currentSort
      }

      if (params.get('page')) {
        const page = Number(params.get('page'))
        this.pageNumber = page || 1
      }
    },

    /**
     * Updates url param with current page number
     * @param {Number} page - number of current page
     */
    updateUrlPageParams(page) {
      const currentUrl = new URL(window.location)

      if (!page || page <= 1) {
        currentUrl.searchParams.delete('page')
        window.history.replaceState({}, '', currentUrl)
        return
      }

      currentUrl.searchParams.set('page', page)
      window.history.replaceState({}, '', currentUrl)
    },

    /**
     * Updates url param with current page number
     * @param {Object} sort - Object containing current sort values
     */
    updateUrlSortParams(sort) {
      const currentUrl = new URL(window.location)

      if (!sort) {
        currentUrl.searchParams.delete('sort')
        window.history.replaceState({}, '', currentUrl)
        return
      }

      currentUrl.searchParams.set('sort', sort.id)
      window.history.replaceState({}, '', currentUrl)
    },

    /**
     * Query collection from handle
     * - Takes page number into account for number of products loaded
     * - Replaces liquid products when data fetch is complete
     * @param {Boolean} keepPrevious - Keep previous results
     */
    async queryCollection(keepPrevious = false) {
      this.state = 'loading'

      if (this.collection.settings.showSingleProduct && !this.processedHandles) {
        this.processedHandles = new Set();
      }

      const first = keepPrevious
        ? this.collection.productsPerPage
        : this.collection.productsPerPage * this.pageNumber

      try {
        const response = await cnvs.Query({
          query: collectionQuery,
          variables: {
            ...this.collectionVariables,
            first,
            sort: this.currentSort.sort,
            reverse: this.currentSort.reverse,
          },
        })

        const collection = formatCollection(response)

        const currentPage = keepPrevious
          ? Number(this.collectionData.currentPage) + 1
          : this.pageNumber

        this.pageNumber = currentPage

        this.filters = collection.filters

        // Group color variant products into 1
        if (this.collection.settings.showSingleProduct) {
          const uniqueProducts = [];

          if (!keepPrevious) {
            this.processedTitles = []
          }

          collection.products.forEach((product) => {
            if (!this.processedTitles.includes(`${product.title}`)) {
              uniqueProducts.push(product)

              this.processedTitles.push(`${product.title}`)
            }
          });

          this.collectionProducts = keepPrevious
            ? this.collectionProducts?.concat(uniqueProducts)
            : uniqueProducts;

        } else {
          this.collectionProducts = keepPrevious
            ? this.collectionProducts?.concat(collection.products)
            : collection.products
        }

        this.collectionData = {
          ...collection?.pageInfo,
          currentPage,
        }

        this.collectionVariables.after =
          collection?.pageInfo?.endCursor || null

        const priceFilter = collection.filters.find(
          (filter) => filter.type === 'PRICE_RANGE',
        )

        if (priceFilter) {
          const priceInput = collection.filters.find(
            (item) => item.id === 'filter.v.price',
          ).values[0].input

          if (priceInput) {
            const { min, max } = priceInput.price

            if (!this.priceRange.value) {
              this.priceRange.value = [min, max]
              this.priceRange.min = min
              this.priceRange.max = max
            }
          }
        } else {
          this.priceRange = null
        }

        if (this.collection.settings.showSingleProduct) {
          this.getMoreProducts()
        }
      } catch (error) {
        cnvs.ReportError('Failed to fetch collection', error)
      } finally {
        this.state = 'ready'
      }
    },

    /**
     * Fetches more products as when we fetch single products the amount of
     * products we show per page can be drastically lower then the pagination
     * we set.
     */
    getMoreProducts() {
      if (
        this.collectionData.hasNextPage &&
        this.collectionProducts.length < this.collection.productsPerPage
      ) {
        this.queryCollection(true)
      }
    },

    /**
     * Toggles the object in the filter array
     * @param {Array} arr - Array to add or remove object from.
     * @param {Object} obj - Object to add or remove from array.
     */
    toggleObjectFromArray(arr, obj) {
      const index = arr.findIndex(
        (item) => JSON.stringify(item) === JSON.stringify(obj),
      )

      if (index === -1) {
        arr.push(obj)
      } else {
        arr.splice(index, 1)
      }
    },

    /**
     * Handle filter change.
     * @param {String} filterInput - Filter input.
     */
    handleFilter(filterInput) {
      if (filterInput.value === 0) {
        return
      }

      this.collectionVariables.after = null
      this.pageNumber = 1

      this.toggleObjectFromArray(
        this.collectionVariables.productFilters,
        filterInput,
      )
      this.queryCollection()
    },

    /**
     * Handle price filter change.
     * @param {Array} value - Price range.
     */
    handlePriceFilter(value) {
      const cursor = this.collectionVariables.after
      this.collectionVariables.after = null
      this.pageNumber = 1

      const [min, max] = value

      const priceFilter = this.collectionVariables.productFilters.find(
        (filter) => filter.price,
      )

      if (priceFilter) {
        priceFilter.price = {
          min,
          max,
        }
      } else {
        this.collectionVariables.productFilters.push({
          price: {
            min,
            max,
          },
        })
      }

      this.queryCollection()
      this.collectionVariables.after = cursor
    },

    /**
     * Sets width of select container based on current selected option.
     */
    getSelectWidth() {
      const mainSelect = this.$refs.mainSelect
      const selectResize = this.$refs.selectResize

      if (!mainSelect || !selectResize) {
        return
      }

      mainSelect.style.width = `${selectResize.clientWidth}px`
    },

    /**
     * Handle sort change.
     */
    handleSort() {
      const cursor = this.collectionVariables.after

      this.collectionVariables.after = null
      this.queryCollection()
      this.collectionVariables.after = cursor
    },

    /**
     * Toggle filters.
     * @param {Number} filterId - Filter ID.
     */
    toggleFilter(filterId) {
      const selectedFilter = this.filters.find(
        (filter) => filter.id === filterId,
      )

      if (!selectedFilter) {
        this.activeFilter = null
        return
      }

      if (this.activeFilter === selectedFilter.id) {
        this.activeFilter = null
        return
      }

      this.activeFilter = selectedFilter.id
    },

    /**
     * Reset filters and close filter drawer.
     */
    resetFilters() {
      if (!this.collectionVariables.productFilters.length) {
        this.closeOverlay('filterDrawer')
        return
      }

      this.collectionVariables.productFilters = []
      this.queryCollection()
      this.closeOverlay('filterDrawer')
    },

    /**
     * Apply filters and close filter drawer.
     */
    applyFilters() {
      this.filterStep = 1
      this.closeOverlay('filterDrawer')
    },

    /**
     * Check if filter is checked.
     * @param {Object} filterValue - Filter value.
     * @returns {Boolean} - True if filter is checked.
     */
    isChecked(filterValue) {
      if (!filterValue || !this.collectionVariables.productFilters) {
        return false
      }

      return this.collectionVariables.productFilters.some((filters) => {
        return JSON.stringify(filterValue) === JSON.stringify(filters)
      })
    },

    /**
     * Check if filter toggle contains selected filters
     * @param {String} filterId - Filter id.
     * @returns {Boolean} - True if filter is selected.
     */
    isSelected(filterId) {
      const productFilters = this.collectionVariables.productFilters
      const selectedFilter = this.filters.find(
        (filter) => filter.id === filterId,
      )

      if (!productFilters.length || !selectedFilter) {
        return false
      }

      return selectedFilter.values?.some(
        (value) => this.isChecked(value.input),
      )
    },
  },
}
</script>

<style lang="scss">
@import "~async/utils/checkbox-input/checkbox-input";
@import "~async/utils/radio-input/radio-input";
@import "~async/product-card/product-card";
@import "@/components/filter-drawer";
@import "./main-collection";
</style>
